import React, { useState, useEffect } from 'react';
import { userAPI, trainingAPI, appointmentAPI } from '../../services/api';
import { formatTime } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAppointments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [expandedTimeSlots, setExpandedTimeSlots] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all users (using high limit to get accurate count)
      const users = await userAPI.getAllUsers(10000, 0);
      const now = new Date();
      const activeUsers = users.filter((user) => {
        const endDate = new Date(user.end_date);
        return endDate >= now;
      });

      // Fetch appointments from today onwards
      const appointments = await appointmentAPI.getAllAppointments(
        new Date().toISOString().split('T')[0],
        100,
        0
      );

      // Filter for today's appointments only
      const today = new Date().toISOString().split('T')[0];
      const todaysAppointments = appointments.filter(
        (apt) => apt.training_date === today
      );

      setStats({
        totalUsers: users.length,
        activeUsers: activeUsers.length,
        totalAppointments: appointments.length,
      });

      setTodayAppointments(todaysAppointments);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format phone number for WhatsApp (convert 05XXXXXXXX to 9725XXXXXXXX)
  const formatPhoneForWhatsApp = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');

    // If starts with 0, replace with 972
    if (cleanPhone.startsWith('0')) {
      return '972' + cleanPhone.substring(1);
    }

    // If already has country code, return as is
    if (cleanPhone.startsWith('972')) {
      return cleanPhone;
    }

    // Otherwise, add 972 prefix
    return '972' + cleanPhone;
  };

  // Send WhatsApp reminder
  const sendWhatsAppReminder = (appointment) => {
    const phone = formatPhoneForWhatsApp(appointment.phone);
    const time = appointment.training_time.substring(0, 5);

    // Create message in Hebrew
    const message = `שלום ${appointment.full_name},\n\nזו תזכורת לאימון שלך היום ב-${time}.\nסוג אימון: ${appointment.training_type}\n\nמחכים לך!\nPilates House 🏋️`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Toggle time slot expansion
  const toggleTimeSlotExpanded = (time) => {
    setExpandedTimeSlots(prev => ({
      ...prev,
      [time]: !prev[time]
    }));
  };

  // Group appointments by time
  const groupAppointmentsByTime = (appointments) => {
    const grouped = {};

    appointments.forEach((apt) => {
      if (!grouped[apt.training_time]) {
        grouped[apt.training_time] = {
          appointments: [],
          maxParticipants: apt.max_participants || 5,
          trainingType: apt.training_type
        };
      }
      grouped[apt.training_time].appointments.push(apt);
      if (apt.max_participants) {
        grouped[apt.training_time].maxParticipants = apt.max_participants;
      }
    });

    // Sort by time
    return Object.keys(grouped)
      .sort()
      .map((time) => ({
        time,
        ...grouped[time]
      }));
  };

  const groupedTodayAppointments = groupAppointmentsByTime(todayAppointments);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>לוח בקרה</h1>
        <p>סקירה כללית של המערכת</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon">👥</div>
          <div className="admin-stat-content">
            <h3>סך הכל משתמשים</h3>
            <p className="admin-stat-number">{stats.totalUsers}</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon">✅</div>
          <div className="admin-stat-content">
            <h3>מנויים פעילים</h3>
            <p className="admin-stat-number">{stats.activeUsers}</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon">📝</div>
          <div className="admin-stat-content">
            <h3>תורים קרובים</h3>
            <p className="admin-stat-number">{stats.totalAppointments}</p>
          </div>
        </div>
      </div>

      {/* Today's Appointments */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>תורים להיום ({todayAppointments.length})</h2>
        </div>

        <div className="admin-today-appointments">
          {groupedTodayAppointments.length > 0 ? (
            <div className="admin-time-slots">
              {groupedTodayAppointments.map(({ time, appointments: timeAppointments, maxParticipants, trainingType }) => {
                const isTimeSlotExpanded = expandedTimeSlots[time] || false;
                const bookedCount = timeAppointments.length;
                const isPast = new Date(`${new Date().toISOString().split('T')[0]} ${time}`) < new Date();

                return (
                  <div
                    key={time}
                    className={`admin-time-slot ${isPast ? 'admin-time-slot-past' : ''}`}
                  >
                    <div
                      className="admin-time-slot-header"
                      onClick={() => toggleTimeSlotExpanded(time)}
                    >
                      <div className="admin-time-slot-info">
                        <span className={`admin-expand-icon small ${isTimeSlotExpanded ? 'expanded' : ''}`}>
                          ▶
                        </span>
                        <span className="admin-time-slot-time">
                          {formatTime(time)}
                        </span>
                        <span className="admin-time-slot-count">
                          {bookedCount}/{maxParticipants}
                        </span>
                        {trainingType && (
                          <span className={`admin-badge admin-badge-${trainingType}`}>
                            {trainingType}
                          </span>
                        )}
                      </div>
                      <div className="admin-time-slot-bar">
                        <div
                          className="admin-time-slot-bar-fill"
                          style={{ width: `${(bookedCount / maxParticipants) * 100}%` }}
                        />
                      </div>
                    </div>

                    {isTimeSlotExpanded && (
                      <div className="admin-time-slot-appointments">
                        {timeAppointments.map((apt, index) => (
                          <div key={index} className="admin-appointment-item">
                            <div className="admin-appointment-item-info">
                              <span className="admin-appointment-item-name">
                                {apt.full_name || 'לא ידוע'}
                              </span>
                              <a
                                href={`tel:${apt.phone}`}
                                className="admin-appointment-item-phone"
                              >
                                {apt.phone}
                              </a>
                            </div>
                            <div className="admin-appointment-item-actions">
                              <button
                                className="admin-btn-whatsapp-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendWhatsAppReminder(apt);
                                }}
                                title="שלח תזכורת WhatsApp"
                              >
                                WhatsApp
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="admin-empty-state">
              <p>אין תורים להיום</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>פעולות מהירות</h2>
        </div>

        <div className="admin-quick-actions">
          <a href="/admin/users" className="admin-action-card">
            <div className="admin-action-icon">👤</div>
            <h3>הוסף משתמש</h3>
            <p>צור חשבון משתמש חדש</p>
          </a>

          <a href="/admin/sessions" className="admin-action-card">
            <div className="admin-action-icon">🏋️</div>
            <h3>הוסף אימון</h3>
            <p>צור אימון חדש</p>
          </a>

          <a href="/admin/notifications" className="admin-action-card">
            <div className="admin-action-icon">📢</div>
            <h3>שלח הודעה</h3>
            <p>שלח הודעה למשתמשים</p>
          </a>

          <a href="/admin/appointments" className="admin-action-card">
            <div className="admin-action-icon">📊</div>
            <h3>צפה בתורים</h3>
            <p>ראה את כל התורים</p>
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
